/* ============================================================================
   ESCRITURA CONFIRMADA

   El hallazgo 1 del análisis: los cuatro registros del SGS se guardaban con
   `usePersistedState`, que devuelve un estado de error — y ninguno lo recogía.

   Si la escritura fallaba —cuota llena, modo privado del navegador, IndexedDB
   bloqueada— **la pantalla seguía mostrando el reporte como si estuviera
   guardado**. El piloto lo escribía, lo veía en la lista, cerraba la
   aplicación. Al volver no estaba. Nadie recibía un aviso.

   Para una auditoría eso es peor que no tener el módulo: un sistema que a
   veces pierde reportes produce un registro que PARECE completo y no lo es.

   ══════════════════════════════════════════════════════════════════════════
   LA REGLA

   **Nada se muestra como guardado hasta que la escritura volvió bien.**

   Se escribe primero, se relee para comprobar, y recién entonces se agrega a
   lo que ve el piloto. Si algo falla, el registro NO aparece en la lista y el
   error se muestra sin poder cerrarlo.

   Releer parece exagerado. No lo es: IndexedDB puede aceptar una transacción y
   fallar al confirmarla, y la promesa resuelve igual. Releer es la única forma
   de saber que el dato está.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/**
 * Guarda una lista de registros y comprueba que quedó.
 *
 * @param {string} clave     dónde se guarda
 * @param {Array}  registros la lista completa
 * @param {object} deps      `{guardar, leer}` — inyectables para probar
 * @returns {Promise<{ok, motivo, guardados}>}
 */
export async function guardarConfirmado(clave, registros, { guardar, leer } = {}) {
  if (typeof guardar !== "function" || typeof leer !== "function") {
    return { ok: false, motivo: "El almacenamiento no está disponible.", guardados: null };
  }
  if (!Array.isArray(registros)) {
    return { ok: false, motivo: "Lo que se intentó guardar no es una lista de registros.", guardados: null };
  }

  try {
    await guardar(clave, registros);
  } catch (e) {
    return { ok: false, motivo: motivoLegible(e), guardados: null };
  }

  /* La relectura. IndexedDB puede aceptar la transacción y fallar al
     confirmarla; la promesa resuelve igual y el dato no está. */
  let vuelta;
  try {
    vuelta = await leer(clave);
  } catch (e) {
    return { ok: false, motivo: `Se escribió pero no se pudo comprobar: ${motivoLegible(e)}`, guardados: null };
  }

  if (!Array.isArray(vuelta)) {
    return { ok: false, motivo: "Se escribió pero al releer no había nada.", guardados: null };
  }

  /* Se compara la CANTIDAD, que es lo que detecta una escritura truncada. No
     se comparan los contenidos: con miles de registros eso sería caro y el
     caso que importa —perder entradas— ya se ve en la cuenta. */
  if (vuelta.length !== registros.length) {
    return {
      ok: false,
      motivo: `Se guardaron ${vuelta.length} de ${registros.length} registros. `
        + "Puede ser falta de espacio.",
      guardados: vuelta,
    };
  }

  return { ok: true, motivo: null, guardados: vuelta };
}

/**
 * Agrega un registro solo si se pudo guardar.
 *
 * Devuelve la lista NUEVA únicamente cuando la escritura se confirmó. Si
 * falla, devuelve la lista ANTERIOR intacta y el motivo: el piloto ve que su
 * reporte no entró, en vez de creer que sí.
 */
export async function agregarConfirmado(clave, listaActual, registroNuevo, deps) {
  const actual = Array.isArray(listaActual) ? listaActual : [];
  if (!registroNuevo?.id) {
    return { ok: false, motivo: "El registro no es válido.", lista: actual };
  }

  const propuesta = [...actual, registroNuevo];
  const r = await guardarConfirmado(clave, propuesta, deps);

  /* Si no se guardó, la lista que se devuelve es la VIEJA. Devolver la nueva
     sería exactamente el error que este módulo corrige. */
  return r.ok
    ? { ok: true, motivo: null, lista: propuesta }
    : { ok: false, motivo: r.motivo, lista: actual };
}

/**
 * Reemplaza un registro (por ejemplo tras cambiar su estado), confirmando.
 *
 * Mismo criterio: si no se guardó, no se muestra el cambio.
 */
export async function reemplazarConfirmado(clave, listaActual, registroNuevo, deps) {
  const actual = Array.isArray(listaActual) ? listaActual : [];
  if (!registroNuevo?.id) {
    return { ok: false, motivo: "El registro no es válido.", lista: actual };
  }

  const existe = actual.some((r) => r?.id === registroNuevo.id);
  if (!existe) {
    return { ok: false, motivo: "Ese registro no está en la lista.", lista: actual };
  }

  const propuesta = actual.map((r) => (r?.id === registroNuevo.id ? registroNuevo : r));
  const r = await guardarConfirmado(clave, propuesta, deps);

  return r.ok
    ? { ok: true, motivo: null, lista: propuesta }
    : { ok: false, motivo: r.motivo, lista: actual };
}

/**
 * Traduce el error del navegador a algo que se entienda.
 *
 * «QuotaExceededError» no le dice nada a un piloto, y sin saber qué pasó no
 * puede arreglarlo.
 */
export function motivoLegible(e) {
  const nombre = e?.name || "";
  const mensaje = String(e?.message || e || "");

  if (/Quota/i.test(nombre) || /quota|exceeded/i.test(mensaje)) {
    return "No hay espacio para guardar. Libera espacio en el dispositivo "
      + "o exporta y archiva los registros viejos.";
  }
  if (/InvalidState|NotAllowed|Security/i.test(nombre)) {
    return "El navegador no permite guardar. Suele pasar en modo privado: "
      + "sal del modo privado para que los registros queden.";
  }
  if (/Version/i.test(nombre)) {
    return "La base de datos quedó de una versión anterior. Cierra las otras "
      + "pestañas de la aplicación y vuelve a intentar.";
  }
  return `No se pudo guardar: ${mensaje}`;
}

/**
 * Comprueba si el almacenamiento sirve, ANTES de que el piloto escriba nada.
 *
 * Descubrir que no se puede guardar después de redactar un reporte largo es la
 * peor forma de enterarse.
 */
export async function comprobarAlmacenamiento({ guardar, leer, borrar } = {}) {
  if (typeof guardar !== "function" || typeof leer !== "function") {
    return { ok: false, motivo: "El almacenamiento no está disponible en este navegador." };
  }

  const clave = "sgs:prueba-escritura";
  const testigo = { t: Date.now() };

  try {
    await guardar(clave, testigo);
    const vuelta = await leer(clave);
    if (typeof borrar === "function") await borrar(clave).catch(() => {});

    if (vuelta?.t !== testigo.t) {
      return { ok: false, motivo: "Se escribió pero al releer el dato no coincidía." };
    }
    return { ok: true, motivo: null };
  } catch (e) {
    return { ok: false, motivo: motivoLegible(e) };
  }
}
